import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import { AIFI_POSITIVE_TEXT_COLOR } from '@/domains/pages/ai-proficiency/constants';
import type { ModelId, ModelTier } from '@/domains/pages/model-savings/types';

/** Savings-positive accent (savings amounts, share rings, pie chart). Same green as AI proficiency positives. */
export const MODEL_SAVINGS_POSITIVE_COLOR = AIFI_POSITIVE_TEXT_COLOR;

/** Negative / worse-than-actual values in calculation tables. */
export const MODEL_SAVINGS_NEGATIVE_COLOR = MULTIPLE_COLORS[2]!;

/** Recommended-mix / optimal sparkles accent. */
export const MODEL_SAVINGS_ACCENT_COLOR = MULTIPLE_COLORS[4]!;

/** Inactive pie segment (remaining spend share). */
export const MODEL_SAVINGS_REMAINDER_COLOR = '#45474a';

export function modelSavingsColorTint(color: string, alphaHex: string): string {
  return `${color}${alphaHex}`;
}

/** Column help text for the savings task table. */
export const SAVINGS_TASK_TABLE_TOOLTIPS = {
  task: 'The type of work employees executed with AI.',
  saving:
    'Annualized spend you would avoid on this task by running each interaction on the cheapest model tier that still fits it. The ring shows that saving as a share of the task spend.',
  spend: 'Annualized AI spend for this task based on its current model mix and list model prices.',
  currentMix:
    'How this task’s catalog cost is distributed across model tiers today. Hover over the chart to see the exact share for each tier.',
  optimalMix:
    'How this task’s catalog cost would be distributed across model tiers using the lowest-cost tier that maintains similar output quality. Hover over the chart to see the exact share for each tier.',
} as const;

/** Column help text for the savings calculation table in the details drawers. */
export const SAVINGS_CALCULATION_TABLE_TOOLTIPS = {
  model: 'The model used for these interactions, as reported by your provider.',
  tier: 'The model tier this model belongs to: Light, Medium, Frontier, or Flagship.',
  actual: 'Annualized AI spend on this model under the current mix, with its share of the mix shown below.',
  optimal: 'Annualized AI spend on this model under the recommended mix, with its share of the mix shown below.',
  change:
    'Difference between recommended and current annualized AI spend. A negative value is a saving; a positive value is additional AI spend.',
} as const;

/** Help text for the labelled sections inside the details drawers. */
export const SAVINGS_DETAILS_SECTION_TOOLTIPS = {
  calculation:
    'Compares annualized AI spend by model under the current mix with AI spend under the recommended mix, based on list model prices. The differences add up to the potential annualized savings shown above.',
  recommendedMix:
    'The share of catalog cost recommended for each model tier to maintain similar output quality at the lowest AI spend.',
} as const;

/** Why these tasks carry a saving, said the same way on the card and in the drawer. */
export const SAVINGS_OPPORTUNITIES_EXPLANATION =
  'These tasks use a more expensive model tier mix than needed. Switching to the recommended mix maintains the same output quality while reducing AI spend by the amount shown';

/** What each tier in the model mix legend means. */
export const MODEL_TIER_TOOLTIPS: Record<ModelTier, string> = {
  light: 'The lowest-cost model tier, suited to short, well-specified tasks that require less model capability.',
  medium: 'The middle model tier, suited to tasks that require more reasoning, drafting, or summarization.',
  frontier: 'A high-capability model tier, suited to tasks that require strong reasoning or generation quality.',
  flagship:
    'The highest-capability model tier, suited to tasks that require the most advanced model performance.',
};

/** Headline figure, at the size and weight of the AI Proficiency score. */
export const MODEL_SAVINGS_KPI_NUMBER_CLASS = 'text-[44px] leading-none font-medium tracking-tight';

/** The same figure treatment one step down, for the two stat boxes inside a drawer. */
export const MODEL_SAVINGS_STAT_NUMBER_CLASS = 'text-2xl leading-none font-medium tracking-tight';

/** The unit that trails a stat-box figure. */
export const MODEL_SAVINGS_STAT_SUFFIX_CLASS = 'text-sm text-muted-foreground';

/** The unit that trails a headline figure, like the score card's "/100". */
export const MODEL_SAVINGS_KPI_SUFFIX_CLASS = 'text-2xl text-muted-foreground';

/** The same unit treatment, scaled for a row-level figure rather than a headline. */
export const MODEL_SAVINGS_ROW_SUFFIX_CLASS = 'text-xs text-muted-foreground';

/**
 * Tier colours use the same MULTIPLE_COLORS indices as AI proficiency levels:
 * Light → [3], Medium → [5], Frontier → [2], Flagship → [7].
 */
export const MODEL_TIER_COLORS: Record<ModelTier, string> = {
  light: MULTIPLE_COLORS[3]!,
  medium: MULTIPLE_COLORS[5]!,
  frontier: MULTIPLE_COLORS[2]!,
  flagship: MULTIPLE_COLORS[7]!,
};

export const MODEL_ID_COLORS: Record<ModelId, string> = {
  haiku: MODEL_TIER_COLORS.light,
  sonnet: MODEL_TIER_COLORS.medium,
  opus: MODEL_TIER_COLORS.frontier,
  flagship: MODEL_TIER_COLORS.flagship,
};

export const MODEL_LABELS: Record<ModelId, string> = {
  haiku: 'Haiku',
  sonnet: 'Sonnet',
  opus: 'Opus',
  flagship: 'Flagship',
};

export const MODEL_TIER_LABELS: Record<ModelTier, string> = {
  light: 'Light',
  medium: 'Medium',
  frontier: 'Frontier',
  flagship: 'Flagship',
};

/** Light to flagship: the order every mix visual and its legend read in. */
export const MODEL_ORDER: ModelId[] = ['haiku', 'sonnet', 'opus', 'flagship'];

export const MODEL_TIERS: Record<ModelId, ModelTier> = {
  haiku: 'light',
  sonnet: 'medium',
  opus: 'frontier',
  flagship: 'flagship',
};
